/**
 * Pure validation for membership visit form state.
 */
import { fromMantineDateString } from '../mantineDateUtils';
import { calendarDateMs } from '../calendarDate';
import { parseDateValue } from '../formatDateWithLocale';
import type { MembershipVisitFormState } from '../trips/buildMembershipVisitRequest';

export interface MembershipVisitValidationErrors {
  locationId?: string;
  startDate?: string;
  endDate?: string;
}

export interface MembershipVisitValidationResult {
  errors: MembershipVisitValidationErrors;
  isValid: boolean;
}

export interface MembershipVisitItineraryLeg {
  id?: number;
  sortOrder: number;
  startDate?: string;
  endDate?: string;
}

export interface MembershipVisitValidationOptions {
  visitId?: number;
  itineraryVisits: MembershipVisitItineraryLeg[];
}

function calendarDateMsFromValue(value: string | undefined): number | null {
  if (!value?.trim()) {
    return null;
  }

  const pickerDate = fromMantineDateString(value);
  if (pickerDate) {
    return calendarDateMs(pickerDate);
  }

  const parsed = parseDateValue(value);
  if (!parsed) {
    return null;
  }

  return calendarDateMs(parsed);
}

function sortItineraryLegs(
  visits: MembershipVisitItineraryLeg[]
): MembershipVisitItineraryLeg[] {
  return [...visits].sort((a, b) => a.sortOrder - b.sortOrder);
}

function validateItineraryContinuity(
  formState: MembershipVisitFormState,
  options: MembershipVisitValidationOptions,
  errors: MembershipVisitValidationErrors
): void {
  const sorted = sortItineraryLegs(options.itineraryVisits);
  const startMs = calendarDateMsFromValue(formState.startDate);
  const endMs = calendarDateMsFromValue(formState.endDate);

  const currentIndex =
    options.visitId != null
      ? sorted.findIndex(visit => visit.id === options.visitId)
      : -1;

  const previous =
    currentIndex > 0
      ? sorted[currentIndex - 1]
      : currentIndex === -1 && sorted.length > 0
        ? sorted[sorted.length - 1]
        : undefined;

  const next =
    currentIndex >= 0 && currentIndex < sorted.length - 1
      ? sorted[currentIndex + 1]
      : undefined;

  if (previous?.endDate && startMs != null) {
    const previousEndMs = calendarDateMsFromValue(previous.endDate);
    if (previousEndMs != null && startMs !== previousEndMs) {
      errors.startDate =
        startMs > previousEndMs
          ? 'Arrival must match the previous leg departure. No gaps allowed'
          : 'Arrival cannot be before the previous leg departure';
    }
  }

  if (next?.startDate && endMs != null) {
    const nextStartMs = calendarDateMsFromValue(next.startDate);
    if (nextStartMs != null && endMs !== nextStartMs) {
      errors.endDate =
        endMs < nextStartMs
          ? 'Departure must match the next leg arrival. No gaps allowed'
          : 'Departure cannot be after the next leg arrival';
    }
  }
}

export function validateMembershipVisit(
  formState: MembershipVisitFormState,
  options?: MembershipVisitValidationOptions
): MembershipVisitValidationResult {
  const errors: MembershipVisitValidationErrors = {};

  if (formState.locationId == null) {
    errors.locationId = 'City is required';
  }

  if (!formState.startDate) {
    errors.startDate = 'Arrival is required';
  }

  if (!formState.endDate) {
    errors.endDate = 'Departure is required';
  }

  if (formState.startDate && formState.endDate) {
    if (formState.startDate >= formState.endDate) {
      errors.endDate = 'Departure must be after arrival';
    }
  }

  if (options) {
    validateItineraryContinuity(formState, options, errors);
  }

  return { errors, isValid: Object.keys(errors).length === 0 };
}
